import { format } from 'date-fns';
import type { AuditTrailRowDto } from '@/services/auditTrailApi';
import { mapWorkflowHistoryToAuditEntries } from '@/hooks/useAuditHistory';
import type { TimesheetWorkflowHistory } from '@/types/workflow-status';
import type {
  AuditStatus,
  WorkflowEventType,
  WorkflowEvent,
  ApproverResponse,
  WorkflowStage,
  AuditTrailDetail,
  AuditTrailRow,
  AuditTrailFilters,
  AuditTrailQuery,
  SortKey,
  SortDir,
} from '@/utils/AuditTrail/types';
import { EVENT_CONFIG, APPROVAL_TYPE_LABELS } from '@/utils/AuditTrail/constants';

// ─── API mapping ──────────────────────────────────────────────────────────────

export const toAuditStatus = (dto: AuditTrailRowDto): AuditStatus => {
  switch (dto.status?.toLowerCase()) {
    case 'approved':
    case 'locked':
      return 'approved';
    case 'rejected':
      return 'rejected';
    case 'draft':
      return dto.wasRecalled ? 'recalled' : 'submitted';
    default:
      return 'submitted';
  }
};

const humanise = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');

export const describeLastEvent = (dto: AuditTrailRowDto): string => {
  if (!dto.lastEventType) return '—';
  const label = EVENT_CONFIG[dto.lastEventType as WorkflowEventType]?.label ?? humanise(dto.lastEventType);
  const actor = dto.lastEventActorName ? ` by ${dto.lastEventActorName}` : '';
  const when = dto.lastEventAt ? ` — ${format(new Date(dto.lastEventAt), 'dd MMM yyyy HH:mm')}` : '';
  return `${label}${actor}${when}`;
};

export const mapAuditTrailRow = (dto: AuditTrailRowDto): AuditTrailRow => ({
  id: String(dto.timesheetId),
  timesheetId: dto.timesheetId,
  employeeName: dto.employeeName ?? `Employee ${dto.employeeId}`,
  employeeId: dto.employeeId,
  weekEnding: new Date(dto.weekEnding),
  department: dto.department ?? '—',
  status: toAuditStatus(dto),
  lastEvent: describeLastEvent(dto),
  lastEventDate: dto.lastEventAt ? new Date(dto.lastEventAt) : undefined,
  totalHours: dto.totalHours,
});

export const toStageStatus = (status: string): WorkflowStage['status'] => {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'completed':
      return 'approved';
    case 'rejected':
      return 'rejected';
    case 'skipped':
      return 'skipped';
    default:
      return 'pending';
  }
};

export const mapHistoryToDetail = (history: TimesheetWorkflowHistory, row: AuditTrailRow): AuditTrailDetail => {
  const events: WorkflowEvent[] = mapWorkflowHistoryToAuditEntries(history)
    .filter((entry) => entry.actionType in EVENT_CONFIG)
    .map((entry) => ({
      id: entry.id,
      type: entry.actionType as WorkflowEventType,
      actorName: entry.actorName,
      timestamp: entry.timestamp,
      reason: entry.actionType === 'submitted' ? undefined : entry.comment,
      delegateName: entry.isDelegated ? entry.actorName : undefined,
      originalApproverName: entry.isDelegated ? entry.delegatedFromManagerName : undefined,
      diff: entry.changes?.map((change) => ({
        field: change.field,
        previousValue: change.oldValue == null ? '—' : String(change.oldValue),
        newValue: change.newValue == null ? '—' : String(change.newValue),
      })),
    }))
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const stages: WorkflowStage[] = [...(history.stages ?? [])]
    .sort((a, b) => a.stageOrder - b.stageOrder)
    .map((stage) => ({
      id: stage.id,
      stageName: stage.name,
      approvalType: stage.approvalType ? APPROVAL_TYPE_LABELS[stage.approvalType] ?? humanise(stage.approvalType) : '—',
      status: toStageStatus(stage.status),
      skippedReason: stage.skippedReason,
      ruleEvaluations: stage.ruleEvaluationLogs?.map((log) => ({
        ruleName: log.ruleName,
        conditionMet: log.conditionMet,
        actionTaken: log.actionTaken ?? '—',
      })),
      approverResponses: (stage.responses ?? [])
        .filter((r) => r.decision === 'approved' || r.decision === 'rejected')
        .map((r) => ({
          approverName: r.approverName ?? 'Unknown',
          decision: r.decision as ApproverResponse['decision'],
          timestamp: r.respondedAt,
          comments: r.comments,
          isDelegated: r.isDelegated,
          delegationFrom: r.delegatedFromName,
        })),
    }));

  const hasApproverResponses = (history.stages ?? []).some((stage) => (stage.responses ?? []).length > 0);
  return { isAutoApproved: row.status === 'approved' && !hasApproverResponses, events, stages };
};

export const toAuditTrailQuery = (
  filters: AuditTrailFilters,
  search: string,
  sortKey: SortKey,
  sortDir: SortDir,
): AuditTrailQuery => ({
  employeeIds: filters.employeeIds,
  projectIds: filters.projectIds?.map(Number).filter((id) => Number.isFinite(id)),
  departments: filters.departments,
  statuses: filters.rejectedOnly
    ? ['rejected']
    : filters.statuses?.flatMap((status) => (status === 'approved' ? ['approved', 'locked'] : [status])),
  billableOnly: filters.billableOnly,
  dateFrom: filters.dateFrom,
  dateTo: filters.dateTo,
  search: search || undefined,
  sortBy: sortKey,
  sortDir,
});
